import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { action, makeObservable, observable } from 'mobx';
import { Provider, inject, observer } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps, Switch } from 'react-router-dom';
import {
	ICreateCampaignRequest,
	ICreateSocialMediaPostRequest,
	IImpersonationContextComponentProps,
	ILocationState,
	IModalContext,
	ModalChildComponentContextKey,
} from '../../../../models';
import { IAdminUserSessionComponentProps } from '../../../../models/AdminModels';
import {
	ErrorMessagesViewModelKey,
	FullScreenModalViewModelKey,
	IErrorMessageComponentProps,
	IFullscreenModalComponentProps,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import { EmailComposerContext, IEmailComposerContext } from '../../../../models/Email';
import { Topics } from '../../../../models/LocalNotificationTopics';
import {
	AccountOperationsViewModel,
	ContentCalendarCreateCampaignApprovalViewModel,
	ImpersonationContextViewModel,
} from '../../../../viewmodels/AdminViewModels';
import { ComposeEmailViewModel } from '../../../../viewmodels/AppViewModels';
import { ISelectOption } from '../../../../web/components/DeprecatedSelect';
import {
	INotificationServiceComponentProps,
	withNotificationService,
} from '../../../../web/components/LocalNotificationObserver/WithNotificationService';
import { MultiContainerHeader } from '../../../../web/components/MultiContainerHeader';
import { PrivateRoute } from '../../../../web/components/PrivateRoute';
import { ComplianceApprovalPrompt } from '../../../../web/components/campaigns/ComplianceApprovalPrompt';
import { EditCampaign, IEditCampaignComponent } from '../../../../web/containers/templates/EditCampaign';
import { EditSendOptions } from '../../../../web/containers/templates/EditSendOptions';
import { BulkEmailApprovalConfirmation } from '../BulkEmailApprovalConfirmation';
import { styleSheet } from './styles';

interface IProps
	extends IEventLoggingComponentProps,
		IAdminUserSessionComponentProps,
		IModalContext,
		IFullscreenModalComponentProps,
		Partial<RouteComponentProps<any>>,
		IErrorMessageComponentProps,
		INotificationServiceComponentProps<Api.ICampaign[]> {
	className?: string;
	createRequest?: ICreateCampaignRequest<Api.ITemplate>;
	styles?: StyleDeclarationValue[];
}

interface IState {
	accountOperations?: AccountOperationsViewModel;
	accountWorkloadForSelectedMonth?: Api.IDayLoad[];
	composerContext?: IEmailComposerContext;
	createRequest?: ICreateCampaignRequest<Api.ITemplate>;
	emailComposer?: ComposeEmailViewModel<Api.IEmailMessageFollowUpOptions>;
	isLoading?: boolean;
	userSelectBoxStyles?: StyleDeclarationValue[];
}

class _CreateContentCalendarCampaign extends React.Component<IProps, IState> {
	private mEditCampaignComponent: IEditCampaignComponent;
	private mApproval?: ContentCalendarCreateCampaignApprovalViewModel;
	private mImpersonationProviderProps: IImpersonationContextComponentProps = {
		impersonationContext: new ImpersonationContextViewModel(),
	};

	constructor(props: IProps) {
		super(props);

		makeObservable<
			_CreateContentCalendarCampaign,
			'mApproval' | 'setSendFromUser' | 'setSendFromOption' | 'onSendWithCompliance' | 'onSendWithOutCompliance'
		>(this, {
			mApproval: observable.ref,
			setSendFromUser: action,
			setSendFromOption: action,
			onSendWithCompliance: action,
			onSendWithOutCompliance: action,
		});

		if (props.createRequest?.sendFromUser) {
			this.mImpersonationProviderProps.impersonationContext.update({
				account: this.mImpersonationProviderProps.impersonationContext.account,
				user: props.createRequest?.sendFromUser,
			});
		}
		const nextState: IState = {
			createRequest: props.createRequest,
			isLoading: true,
		};
		this.state = nextState;
	}

	public async componentDidMount() {
		const { logApiError, logEvent, errorMessages, userSession, history, location, match } = this.props;
		const { createRequest } = this.state;
		const accountId: string = match.params?.accountId;
		let accountOps: AccountOperationsViewModel = null;
		if (accountId) {
			accountOps = new AccountOperationsViewModel(userSession, {
				id: accountId,
			});
			try {
				logEvent('AccountOperationsLoad');
				await accountOps.load();
			} catch (error) {
				logApiError('AccountOperationsLoad-Error', error);
				errorMessages.pushApiError(error);
			}
		}

		const impersonationContext: Api.IImpersonationContext = accountOps
			? {
					account: accountOps.account,
					user: this.mImpersonationProviderProps.impersonationContext.user,
				}
			: undefined;
		this.mImpersonationProviderProps.impersonationContext.update(impersonationContext);

		this.mApproval = new ContentCalendarCreateCampaignApprovalViewModel(userSession, accountOps?.account);
		this.setSendFromUser(this.mImpersonationProviderProps.impersonationContext.user);
		this.mApproval.bulkEmailComposer.emailMessage.setContentWithTemplate(createRequest?.context);
		this.mApproval.bulkEmailComposer.emailMessage.options.scheduledSend.expirationDate =
			createRequest?.context?.schedule?.expirationDate;
		try {
			logEvent('AdminsLoad');
			await this.mApproval.loadAdmins();
		} catch (error) {
			logApiError('AdminsLoad-Error', error);
		}

		this.setState(
			{
				accountOperations: accountOps,
				composerContext: {
					emailComposer: this.mApproval.bulkEmailComposer,
				},
				emailComposer: this.mApproval.bulkEmailComposer as ComposeEmailViewModel<Api.IEmailMessageFollowUpOptions>,
				isLoading: false,
			},
			() => {
				if (location.pathname?.toLocaleLowerCase?.().endsWith('create')) {
					history?.replace({
						pathname: `${location.pathname || ''}/tags`,
						search: location.search,
						state: location.state,
					});
				}
			}
		);
	}

	public render() {
		const { className, styles, match, userSession } = this.props;
		const { isLoading, emailComposer, composerContext } = this.state;
		return (
			<EmailComposerContext.Provider value={composerContext}>
				<div
					className={`${css(styleSheet.container, ...(styles || []))} create-content-calendar-campaign ${
						className || ''
					}`}
				>
					{!emailComposer?.isUserEditingMessage && <MultiContainerHeader fullscreenHeader='View Campaign' />}
					{!!isLoading || !this.mImpersonationProviderProps?.impersonationContext?.isValid ? null : (
						<Provider {...this.mImpersonationProviderProps}>
							<Switch>
								<PrivateRoute
									exact={true}
									path={`${match.url}/confirmation`}
									render={this.onRenderConfirmation}
									userSession={userSession}
								/>
								<PrivateRoute
									exact={true}
									path={`${match.url}/send-options`}
									render={this.onRenderScheduleSend}
									userSession={userSession}
								/>
								<PrivateRoute
									exact
									path={`${match.url}/compliance-options`}
									render={this.onRenderComplianceOptions}
									userSession={userSession}
								/>
								<PrivateRoute render={this.onRenderComposer} userSession={userSession} />
							</Switch>
						</Provider>
					)}
				</div>
			</EmailComposerContext.Provider>
		);
	}

	private onRenderComposer = (props: RouteComponentProps<any>) => {
		const { location } = this.props;
		const { emailComposer } = this.state;
		const locationState: ILocationState<any, any> = location.state;

		return (
			<EditCampaign
				{...props}
				canImpersonateOnTemplateSave={locationState?.referrer === 'contentCalendar'}
				emailComposer={emailComposer}
				initialEmailBodyTemplate={this.state?.createRequest?.context}
				onInnerRef={this.onEditCampaignRef}
				sendFromAdminsProps={{
					admins: this.mApproval?.admins,
					initialSendFromUser: this.props.createRequest?.sendFromUser,
					userSession: this.props.userSession,
					impersonationContext: this.mImpersonationProviderProps?.impersonationContext,
					sendOnBehalfOf: this.onUserOptionSelected,
					allowConnectionTypes: true,
				}}
				onSocialMediaPostBannerClicked={this.onSocialMediaPostBannerClicked}
				styles={[styleSheet.childRoute]}
			/>
		);
	};

	private onSocialMediaPostBannerClicked = (e: React.MouseEvent<HTMLElement>) => {
		const { emailComposer, accountOperations, createRequest } = this.state;
		const { history } = this.props;
		e.preventDefault();
		const templateId = emailComposer.emailMessage?.templateReference?.templateId;
		const locationState: ILocationState<any, ICreateSocialMediaPostRequest> = createRequest
			? {
					model: {
						context: templateId,
						schedule: createRequest.schedule,
						type: 'TemplateId',
					},
				}
			: null;
		history.replace({
			pathname: `/accounts/${accountOperations.account.id}/content-calendar/social-media/post/edit${
				templateId ? `-template/${templateId}` : ''
			}`,
			state: locationState,
		});
	};

	private onEditCampaignRef = (ref?: IEditCampaignComponent) => {
		this.mEditCampaignComponent = ref;
	};

	private onUserOptionSelected = (
		option: ISelectOption<string | Api.UserViewModel<Api.IUser>>,
		user?: Api.UserViewModel<Api.IUser>
	) => {
		const contactOwnerOptionChosen = option?.dataContext === Api.SendEmailFrom.ContactOwner;
		const isConnectionType =
			typeof option?.dataContext === 'string' && option.dataContext.startsWith('connection-type-');

		if (isConnectionType) {
			// Handle connection type selection
			const connectionTypeValue = (option.dataContext as string).replace('connection-type-', '');
			this.setSendFromOption({
				mode: Api.SendEmailFrom.ConnectionType,
				connectionType: connectionTypeValue,
			});
			this.mEditCampaignComponent?.resetSignature?.(null);
		} else {
			// Handle existing options (contact owners, employees)
			this.setSendFromUser(contactOwnerOptionChosen ? null : user);
			this.mEditCampaignComponent?.resetSignature?.(contactOwnerOptionChosen ? null : user.id);
		}
	};

	private onRenderScheduleSend = (props: RouteComponentProps<any>) => {
		const { accountOperations, emailComposer, createRequest } = this.state;
		return (
			<EditSendOptions
				{...props}
				disableSendNow={true}
				emailComposer={emailComposer}
				hideCampaignsList={true}
				initialStartDate={createRequest?.schedule?.startDate}
				onScheduleCtaClicked={this.onScheduleCtaClicked}
				requireEstimateToSend={false}
				sendEmail={accountOperations.account.features.sendEmail}
			/>
		);
	};

	private onRenderConfirmation = (props: RouteComponentProps<any>) => {
		return (
			<BulkEmailApprovalConfirmation
				{...props}
				approval={this.mApproval}
				customMessaging='This email will not be scheduled until the client visits the content calendar section and clicks a button to acknowledge the content.'
				hideHeader={true}
				onRequestClose={this.onConfirmationComplete}
			/>
		);
	};

	private onRenderComplianceOptions = (props: RouteComponentProps<any>) => {
		return (
			<ComplianceApprovalPrompt
				{...props}
				onScheduleCompliance={(e: React.MouseEvent<HTMLElement>, sendWithComplianceEmail: string) => {
					e.preventDefault();
					this.onSendWithCompliance(sendWithComplianceEmail);
				}}
				onScheduleWithoutCompliance={this.onSendWithOutCompliance}
			/>
		);
	};

	private onScheduleCtaClicked = async () => {
		const { logApiError, logEvent, postNotification, history, match, errorMessages } = this.props;
		const { createRequest } = this.state;
		try {
			logEvent('ScheduleCampaign');
			const campaign = await this.mApproval?.createApproval(this.mImpersonationProviderProps.impersonationContext.user);
			postNotification({
				info: [campaign.toJs()],
				topic: Topics.EDIT_CAMPAIGNS_ITEM,
			});
			history?.replace({
				pathname: `${match.url}/confirmation`,
				search: history?.location?.search,
				state: history?.location?.state,
			});
			createRequest?.onFinish?.(true);
		} catch (error) {
			logApiError('ScheduleCampaign-Error', error);
			errorMessages?.pushApiError(error);
		}
	};

	private onConfirmationComplete = () => {
		const { fullscreenModal } = this.props;
		// Update the group email VM here depending on how the info is served from the API.
		fullscreenModal?.history.goBackToLocationBeforeTrackedHistory(true);
	};

	private setSendFromUser = (user?: Partial<Api.IUser>) => {
		const emailOptions = this.mApproval.bulkEmailComposer.emailMessage.options;
		this.mImpersonationProviderProps.impersonationContext.update({
			account: this.mImpersonationProviderProps.impersonationContext.account,
			user: user ?? null,
		});
		emailOptions.sendEmailFrom = user ? Api.SendEmailFrom.SelectedUser : Api.SendEmailFrom.ContactOwner;
		emailOptions.sendEmailFromUser = user;
		emailOptions.sendEmailFromUserId = user?.id;
	};

	/**
	 * Sets the send from option for the email campaign, handling both connection types and other send modes.
	 * @param sendFromOption - The send from configuration with mode and optional connectionType
	 */
	private setSendFromOption = (sendFromOption: { mode: Api.ISendEmailFrom; connectionType?: string }) => {
		const emailOptions = this.mApproval.bulkEmailComposer.emailMessage.options;
		emailOptions.sendEmailFrom = sendFromOption.mode;

		if (sendFromOption.mode === Api.SendEmailFrom.ConnectionType) {
			if (!sendFromOption.connectionType) {
				throw new Error('connectionType is required when mode is ConnectionType');
			}
			emailOptions.sendFromConnectionType = sendFromOption.connectionType;
			// Clear user-specific options when using connection type
			emailOptions.sendEmailFromUser = null;
			emailOptions.sendEmailFromUserId = null;
		}
	};

	private onSendWithCompliance = (sendWithComplianceEmail: string) => {
		const { history, match } = this.props;
		const { emailComposer } = this.state;
		emailComposer.emailMessage.options = emailComposer.emailMessage.options || {};
		emailComposer.emailMessage.options.sendWithCompliance = true;
		emailComposer.emailMessage.options.sendWithComplianceEmail = sendWithComplianceEmail;
		history?.push(`${match.url}/send-options`);
	};

	private onSendWithOutCompliance = () => {
		const { history, match } = this.props;
		const { emailComposer } = this.state;
		emailComposer.emailMessage.options = emailComposer.emailMessage.options || {};
		emailComposer.emailMessage.options.sendWithCompliance = false;
		history?.push(`${match.url}/send-options`);
	};
}

const CreateContentCalendarCampaignAsObserver = observer(_CreateContentCalendarCampaign);
const CreateContentCalendarCampaignWithContext = inject(
	UserSessionViewModelKey,
	FullScreenModalViewModelKey,
	ModalChildComponentContextKey,
	ErrorMessagesViewModelKey
)(CreateContentCalendarCampaignAsObserver);
const CreateContentCalendarCampaignWithNotificationService = withNotificationService(
	CreateContentCalendarCampaignWithContext
);
export const CreateContentCalendarCampaign = withEventLogging(
	CreateContentCalendarCampaignWithNotificationService,
	'CreateContentCalendarCampaign'
);
